import { DataType, DateTime, ExprCall } from '@mode-switch/express';
import { Attr } from '@mode-switch/model';
import {
  EncContinuousDateFilterField,
  EncContinuousFilterField,
  EncFilterField,
  EncRelativeDateFilter,
  EncRelativeDateFilterField,
  EncTopByFieldCondition,
  Encoding,
  FilterType,
  SelectionMode,
  VariableType,
} from '@mode-vegas/vegas';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { HashKey, generateUUID } from '@mode/shared/util-js';
import { CompiledField, CompiledFormula, FormulaOperations } from '@mode/shared/util-switch';
import * as FieldHelpers from '../helpers/field-helpers';
import { FilterStep } from './filter-modal.component';

/**
 * Determines if the formula can be used in a TopN filter
 */
export function supportsTopNFilter(field: CompiledFormula) {
  return field.isAggregateable && field.attrDataType !== DataType.Name.Boolean;
}

/**
 *  Helper function to generate a stable id from formulas for domain plans
 */
export function idFromFormula(source: string): string {
  return `D${HashKey.hashFn(source).toString(16).padStart(8, '0')}`;
}

/**
 * @description Create a short uuid as a local replacement for @mode-vegas/vegas's BField ID function.
 * These will not conflict with the ids generated in vegas as these are 8 chars instead of 6
 */
export function shortId() {
  return generateUUID().slice(0, 8);
}

/**
 * Returns the filter types which are allowed for the given FilterStep
 */
export function getFilterTypesForStep(step: FilterStep) {
  switch (step) {
    case FilterStep.MultiValues:
      return [FilterType.Discrete, FilterType.DiscreteBoolean];
    case FilterStep.TopByField:
      return [FilterType.TopByField];
    case FilterStep.Range:
      return [FilterType.Continuous, FilterType.ContinuousDate];
    case FilterStep.RelativeDateRange:
      return [FilterType.RelativeDate];
    default:
      return [];
  }
}

/**
 * Builds the default empty filter appropriate for the given field and step
 */
export function makeEmptyFilter(
  field: CompiledField,
  view: FlamingoTypes.ModeView,
  encoding: Encoding,
  step: FilterStep
) {
  switch (step) {
    case FilterStep.MultiValues:
      return makeMultiValuesFilter(field);
    case FilterStep.TopByField:
      return makeTopByFieldFilter(view, encoding, field);
    case FilterStep.Range: {
      return makeContinuousRangeFilter(field);
    }
    case FilterStep.RelativeDateRange: {
      return makeRelativeDateRangeFilter(field);
    }
  }
}

function makeMultiValuesFilter(field: CompiledField): EncFilterField {
  return {
    filterType: field.formulaDataType === DataType.Name.Boolean ? FilterType.DiscreteBoolean : FilterType.Discrete,
    formula: field.formula,
    selection: {
      selected: [],
      mode: SelectionMode.ALL,
    },
    showRelevantValuesOnly: false,
    variable: { type: VariableType.DISCRETE },
  };
}

function makeTopByFieldFilter(view: FlamingoTypes.ModeView, encoding: Encoding, field: CompiledField): EncFilterField {
  const [encodingByFieldAttrs, otherByFieldAttrs] = FieldHelpers.makePartitionedByFieldsLists(view, encoding);
  const byFieldAttr = encodingByFieldAttrs[0] ?? otherByFieldAttrs[0];

  // If the byField matches the filter field, then we can have the special 'No Aggregation' case.
  const isByFieldMatchFilterField = byFieldAttr?.value.attrName === field.attrName;
  const allowedAggrFuncs = FieldHelpers.aggFnsForFormula(byFieldAttr.value, isByFieldMatchFilterField);
  const aggrFunc = allowedAggrFuncs[0];

  let byFieldFormula = `[${byFieldAttr.value.attrName}]`;
  if (ExprCall.Name.isKnown(aggrFunc.value ?? '')) {
    byFieldFormula = `${aggrFunc.value}(${byFieldFormula})`;
  }

  return {
    filterType: FilterType.TopByField,
    formula: field.formula,
    variable: { type: VariableType.DISCRETE },
    cond: {
      byField: {
        id: shortId(),
        formula: byFieldFormula,
        variable: {
          type: isByFieldMatchFilterField ? VariableType.DISCRETE : VariableType.CONTINUOUS,
        },
      },
      operator: EncTopByFieldCondition.Operator.Top,
      sequence: EncTopByFieldCondition.Sequence.LastIsolate,
      limit: 10,
    },
  };
}

function makeContinuousRangeFilter(field: CompiledField): EncFilterField {
  const isDateType =
    field.formulaDataType === DataType.Name.DateTime || field.formulaDataType === DataType.Name.Timestamp;

  const filterFieldParams = {
    formula: field.formula,
    variable: { type: VariableType.CONTINUOUS },
    range: {
      min: null,
      max: null,
    },
    includeMin: true,
    includeMax: !isDateType,
    includeNulls: false,
  };

  if (isDateType) {
    return {
      filterType: FilterType.ContinuousDate,
      ...filterFieldParams,
    } as unknown as EncContinuousDateFilterField;
  } else {
    return {
      filterType: FilterType.Continuous,
      ...filterFieldParams,
    } as unknown as EncContinuousFilterField;
  }
}

export function makeRelativeDateRangeFilter(field: CompiledField): EncRelativeDateFilterField {
  const yearStart = DateTime.yearStartMonth(field.yearStart);
  const weekStart = DateTime.weekStartDow(field.weekStart);
  return {
    filterType: FilterType.RelativeDate,
    variable: { type: VariableType.CONTINUOUS },
    formula: field.formula,
    includeNulls: false,
    from: {
      type: 'fixed',
      value: NaN,
    },
    to: {
      type: 'fixed',
      value: NaN,
    },
    yearStart,
    weekStart,
  };
}

export function convertContinuousDateToRelativeDate(
  filter: EncContinuousDateFilterField,
  field: CompiledField
): EncRelativeDateFilterField {
  // ~53 CONTINUOUS_DATE filters have DATEPART in their formulas.. which is incorrect.
  // We'll reset these the first time they're converted
  if (field.hasDatePart) {
    return makeRelativeDateRangeFilter(makeRelativeDateFilterField(field));
  }

  let from: EncRelativeDateFilter.FromTo = Number.isSafeInteger(filter.range.min)
    ? { type: 'fixed', value: filter.range.min }
    : { type: 'unset' };

  let to: EncRelativeDateFilter.FromTo = Number.isSafeInteger(filter.range.max)
    ? { type: 'fixed', value: filter.range.max }
    : { type: 'unset' };

  // If the range was empty then init with a placeholder fixed date. NaN
  // will be swapped out with the actual min/max dates (if any)
  if (from.type === 'unset' && to.type === 'unset') {
    from = { type: 'fixed', value: NaN };
    to = { type: 'fixed', value: NaN };
  }

  return {
    filterType: FilterType.RelativeDate,
    variable: { type: VariableType.CONTINUOUS },
    includeNulls: filter.includeNulls,
    formula: field.formula,
    from,
    to,
  };
}

export function makeRelativeDateFilterField(field: CompiledFormula): CompiledField {
  return field.withNewFormula(
    field.dateFuncUnit
      ? // normalize datepart|datetrunc -> datetrunc
        FormulaOperations.dateTrunc(field, field.dateFuncUnit)
      : // access pattern
        FormulaOperations.access(field),
    Attr.Type.Continuous
  );
}
